import { useContext } from 'react';
import { format, isValid } from 'date-fns';
import useFetchLeadsPaginated from 'components/domain/lead/useFetchLeadsPaginated';
import {
  HostfullyLeadChannel,
  LeadChannel,
  LeadSource,
  LeadStatus,
} from 'models/Leads';
import { Property } from 'models/Properties';
import { getPropertiesFilterConditions } from 'utils/pipeline/pipelineUtils';
import PipelineContext from './PipelineContext';
import { PipelineFilter } from './Pipeline.types';
import { getPropertyUidsMatchingTags } from './filter/PipelineFilters.utils';

const PIPELINE_CHANNEL_OPTION_TO_GRAPHQL_FILTER_MAPPING = {
  [HostfullyLeadChannel.HOSTFULLY_API]: {
    channels: [],
    sources: [LeadSource.ORBIRENTAL_API],
  },
  [HostfullyLeadChannel.HOSTFULLY_DIRECT_BOOKING]: {
    channels: [LeadChannel.HOSTFULLY],
    sources: [LeadSource.ORBIRENTAL_WIDGET],
  },
  [HostfullyLeadChannel.HOSTFULLY_PMP]: {
    channels: [LeadChannel.HOSTFULLY],
    sources: [LeadSource.ORBIRENTAL_FORM],
  },
};

const getChannelsAndSources = (
  pipelineChannels: PipelineFilter['channels'],
) => {
  return Array.from(pipelineChannels).reduce(
    (acc, pipelineChannelOption) => {
      const mapping =
        PIPELINE_CHANNEL_OPTION_TO_GRAPHQL_FILTER_MAPPING[
          pipelineChannelOption as HostfullyLeadChannel
        ];

      if (mapping) {
        return {
          channels: [...acc.channels, ...mapping.channels],
          sources: [...acc.sources, ...mapping.sources],
        };
      }

      return {
        channels: [...acc.channels, pipelineChannelOption as LeadChannel],
        sources: acc.sources,
      };
    },
    { channels: [] as LeadChannel[], sources: [] as LeadSource[] },
  );
};

function getLeadsQueryConditions(
  filter: PipelineFilter,
  properties: Property[],
) {
  const {
    channels: pipelineChannels,
    checkInFrom,
    checkInTo,
    groupedStatus,
    sortStrategy,
    statuses,
    uids,
    userUids,
  } = filter;

  if (uids?.length) {
    return `uids:[${Array.from(uids)
      .map((uid) => `"${uid}"`)
      .join(',')}]`;
  }

  const { channels, sources } = getChannelsAndSources(pipelineChannels);

  return `
    ${channels?.length ? `channels:[${Array.from(channels).join(',')}],` : ''}
    ${sources?.length ? `sources:[${Array.from(sources).join(',')}],` : ''}
    ${
      isValid(checkInFrom)
        ? `checkin_from:"${format(checkInFrom, 'yyyy-MM-dd')}",`
        : ''
    }
    ${
      isValid(checkInTo)
        ? `checkin_to:"${format(checkInTo, 'yyyy-MM-dd')}",`
        : ''
    }
    ${groupedStatus ? `groupedStatus:${groupedStatus},` : ''}
    ${getPropertiesFilterConditions(filter, properties)}
    ${statuses?.length ? `statuses:[${statuses.join(',')}],` : ''}
    ${
      userUids?.length
        ? `userUids:[${Array.from(userUids)
            .map((userUid) => `"${userUid}"`)
            .join(',')}],`
        : ''
    }
    hideSubLeads:true,
    sortStrategy:"${sortStrategy}"
    statusesToExclude:[${LeadStatus.BLOCKED}]
  `.trim();
}

const usePipelineLeadsListData = () => {
  const { filter, properties } = useContext(PipelineContext);

  // A selected property takes precedence; otherwise expand tags into property uids.
  const effectiveFilter: PipelineFilter = filter.propertyUids?.length
    ? filter
    : {
        ...filter,
        propertyUids: getPropertyUidsMatchingTags(
          properties,
          filter.propertyTags,
        ),
      };

  const conditions = getLeadsQueryConditions(effectiveFilter, properties);
  const fields = `assignee{bestProfile, uid, picture},
      availablePipelineActions,
      channel{bestProfile, channel},
      affiliate,
      uid,
      firstName,
      lastName, 
      email, 
      source,
      leadType,
      bookedDateTimeString,
      createdDateTimeString,
      checkInDateTimeString,
      checkInLocalDateTime, 
      checkOutLocalDateTime, 
      childrenCount, 
      adultCount, 
      petCount, 
      infantCount, 
      status,
      referrer,
      hasCompletedPreArrivalForm,
      agreementSigned,
      autoRefundFail,
      autoRefundOk,
      wasSecurityDepositReceived,
      wereCheckInInstructionsSent,
      paidInFull,
      property{uid, name, businessType, mainPicture {
        largeThumbnail
      }},
      creator{type},
      countdown {
        color,
        countdownMinutes,
        countdownPercentage,
        overdue,
        visualCountdownValue
      },
      riskScore,
      order {
        balance,
        currency,
        currencySymbol,
        securityDeposit,
        totalAmount,
        uid
      },
      hotel { 
        name,
        uid 
      },
      duplicateOfLeadUid,
      hotelUid,
      groupUid,
      isGroupBooking,
      duplicateOfLeadUid,
      airbnbAlteration {
        uid
        startDate
        endDate
        type
        price
        nights
      }
      `;

  const {
    error,
    isFetching,
    isInitialFetching,
    leads,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useFetchLeadsPaginated({
    conditions,
    fields,
  });

  return {
    error,
    isFetching,
    isInitialFetching,
    leads,
    hasMoreLeads: hasNextPage,
    loadMoreLeads: fetchNextPage,
    refetch,
  };
};

export default usePipelineLeadsListData;
